import { api } from "@/utils/api";
import { toast } from "@/utils/appToast";
import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { useTranslation } from 'react-i18next';
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import GoogleSignUp from '@/utils/googleSignup'
import RegistrationConfirmationModal from "@/components/common/RegistrationConfirmationModal";
import { createMetaConversionPayload, fireMetaPixelEvent } from "@/utils/metaPixel";

const SignupCustomerPage = () => {
    const { t } = useTranslation();
    const [formValues, setFormValues] = useState({
        first_name: "",
        last_name: "",
        email: "",
        password: "",
        repeatPassword: "",
        termofuse: false,
    });
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [smsNotificationsOptIn, setSmsNotificationsOptIn] = useState(false);
    const [showPassword, setShowPassword] = useState({
        password: false,
        repeatPassword: false,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const showConfirmationOnce = (identifier: string) => {
        const storageKey = `signup-confirmation:${identifier}`;
        if (sessionStorage.getItem(storageKey)) return;
        sessionStorage.setItem(storageKey, "1");
        setShowConfirmationModal(true);
    };

    const emailValidationError =
        formValues.email.trim() !== "" && !validateEmail(formValues.email) ? t("toastValidEmail") : "";
    const passwordValidationError =
        formValues.password.trim() !== "" && formValues.password.length < 6 ? t("toastPasswordMin6") : "";
    const passwordMatchValidationError =
        formValues.repeatPassword.trim() !== "" && formValues.password !== formValues.repeatPassword
            ? t("PasswordDontMatch")
            : "";

    const isRegisterFormValid =
        formValues.first_name.trim() !== "" &&
        formValues.last_name.trim() !== "" &&
        formValues.email.trim() !== "" &&
        emailValidationError === "" &&
        formValues.password.trim() !== "" &&
        passwordValidationError === "" &&
        formValues.repeatPassword.trim() !== "" &&
        passwordMatchValidationError === "" &&
        formValues.termofuse &&
        phone.replace(/\D/g, '').length >= 11;

    const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const registerForm = e.target as HTMLFormElement;
        const registerData = new FormData(registerForm);

        if (!phone || phone.replace(/\D/g, '').length < 11) {
            toast.error(t("toastValidPhone"));
            setPhoneError(t("toastValidPhone"));
            return;
        }

        const email = registerData.get("email") as string;
        if (!email || !validateEmail(email)) {
            toast.error(t("toastValidEmail"));
            return;
        }

        if (registerData.get("password") !== registerData.get("repeatPassword")) {
            toast.error(t("PasswordDontMatch"));
            return;
        }

        const password = registerData.get("password") as string;
        if (password.length < 6) {
            toast.error(t("toastPasswordMin6"));
            return;
        }

        const newUser = {
            first_name: registerData.get("first_name") as string,
            last_name: registerData.get("last_name") as string,
            email: registerData.get("email") as string,
            password: registerData.get("password") as string,
            phone: '+' + phone,
            isSmsSent: smsNotificationsOptIn,
        };

        setIsLoading(true);
        try {
            const meta = createMetaConversionPayload();
            const { status, data } = await api.signUp({
                ...newUser,
                ...meta,
            } as any);
            setIsLoading(false);

            if (status === "error") return toast.error(data.message);

            const { user } = data;

            fireMetaPixelEvent("CompleteRegistration", meta.eventId);

            registerForm.reset();
            setFormValues({
                first_name: "",
                last_name: "",
                email: "",
                password: "",
                repeatPassword: "",
                termofuse: false,
            });
            setPhone('');
            setPhoneError('');
            setSmsNotificationsOptIn(false);
            showConfirmationOnce(user?._id || newUser.email);
        } catch (error) {
            setIsLoading(false);
            toast.error(t("toastRegisterUserError"));
        }
    };

    const fields = [
        {
            id: "registerUserEmail",
            type: "text",
            name: "email",
            placeholder: "Email",
            label: "Email",
        },
        {
            id: "registerUserPassword",
            type: "password",
            name: "password",
            placeholder: "Contraseña",
            label: "Contraseña",
        },
        {
            id: "registerUserRepeatPassword",
            type: "password",
            name: "repeatPassword",
            placeholder: "Repetir Contraseña",
            label: "Repetir Contraseña",
        },
    ];

    return (
        <>
        <div
            style={{
                minHeight: "100vh",
                backgroundColor: "#f5f5f3",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                padding: "20px",
                paddingTop: "120px",
            }}
        >
            <div
                style={{
                    backgroundColor: "white",
                    borderRadius: "24px",
                    padding: isMobile ? "32px 24px" : "40px 48px",
                    maxWidth: "600px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                {/* Heading */}
                <div style={{ textAlign: "center", marginBottom: "32px" }}>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            marginBottom: "12px",
                        }}
                    >
                        <img
                            src="/images/phone_symbol.svg"
                            alt="Phone Symbol"
                            style={{
                                width: '50px',
                                height: '50px',
                                display: 'block'
                            }}
                        />
                    </div>
                    <h1
                        style={{
                            fontSize: isMobile ? "24px" : "28px",
                            fontWeight: 600,
                            color: "#1e293b",
                            margin: 0,
                            lineHeight: "1.4",
                        }}
                    >
                        {t("signupAsCustomer")}
                    </h1>
                </div>

                <form onSubmit={handleRegister}>
                    <div className="form-group" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
                        {/* First Name and Last Name in same row */}
                        <div className="row" style={{ marginBottom: 0 }}>
                            <div className="col-md-6">
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label htmlFor="registerUserName" style={{ color: "#1e293b" }}>
                                        {t('firstName')} <span style={{ color: "red" }}> *</span>
                                    </label>
                                    <input
                                        id="registerUserName"
                                        name="first_name"
                                        type="text"
                                        value={formValues.first_name}
                                        onChange={(e) => setFormValues((prev) => ({ ...prev, first_name: e.target.value }))}
                                        placeholder={t('firstName')}
                                        style={{ borderRadius: "8px", border: "1px solid #e5e7eb", minHeight: "48px", padding: "12px 16px" }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group" style={{ marginBottom: 0 }}>
                                    <label htmlFor="registerUserLastname" style={{ color: "#1e293b" }}>
                                        {t('lastName')} <span style={{ color: "red" }}> *</span>
                                    </label>
                                    <input
                                        id="registerUserLastname"
                                        name="last_name"
                                        type="text"
                                        value={formValues.last_name}
                                        onChange={(e) => setFormValues((prev) => ({ ...prev, last_name: e.target.value }))}
                                        placeholder={t('lastName')}
                                        style={{ borderRadius: "8px", border: "1px solid #e5e7eb", minHeight: "48px", padding: "12px 16px" }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Email field */}
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label htmlFor="registerUserEmail" style={{ color: "#1e293b" }}>
                                {t('email')} <span style={{ color: "red" }}> *</span>
                            </label>
                            <input
                                id="registerUserEmail"
                                name="email"
                                type="email"
                                value={formValues.email}
                                onChange={(e) => setFormValues((prev) => ({ ...prev, email: e.target.value }))}
                                placeholder={t('email')}
                                className="form-control"
                                autoComplete="email"
                                style={{ borderRadius: "8px", border: "1px solid #e5e7eb" }}
                            />
                            {emailValidationError && (
                                <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "6px", marginBottom: 0 }}>
                                    {emailValidationError}
                                </p>
                            )}
                        </div>

                        {/* Password fields */}
                        {fields.slice(1).map((field) => {
                            const isPasswordField =
                                field.name === "password" || field.name === "repeatPassword";

                            return (
                                <div key={field.id} className="form-group" style={{ marginBottom: 0 }}>
                                    <label htmlFor={field.id} style={{ color: "#1e293b" }}>
                                        {t(field.name)} <span style={{ color: "red" }}> *</span>
                                    </label>

                                    {isPasswordField && (
                                        <div style={{ position: "relative" }}>
                                            <input
                                                id={field.id}
                                                name={field.name}
                                                type={showPassword[field.name] ? "text" : "password"}
                                                value={field.name === "password" ? formValues.password : formValues.repeatPassword}
                                                onChange={(e) => {
                                                    if (field.name === "password") {
                                                        setFormValues((prev) => ({ ...prev, password: e.target.value }));
                                                    } else {
                                                        setFormValues((prev) => ({ ...prev, repeatPassword: e.target.value }));
                                                    }
                                                }}
                                                placeholder={t(field.name)}
                                                className="form-control"
                                                style={{ paddingRight: "45px", borderRadius: "8px", border: "1px solid #e5e7eb" }}
                                                autoComplete="new-password"
                                            />

                                            <button
                                                type="button"
                                                onClick={() =>
                                                    setShowPassword((prev) => ({
                                                        ...prev,
                                                        [field.name]: !prev[field.name],
                                                    }))
                                                }
                                                style={{
                                                    position: "absolute",
                                                    right: "10px",
                                                    top: "50%",
                                                    transform: "translateY(-50%)",
                                                    background: "none",
                                                    border: "none",
                                                    cursor: "pointer",
                                                    padding: 0,
                                                }}
                                            >
                                                {showPassword[field.name] ? (
                                                    <VisibilityOff style={{ color: "#64748b" }} />
                                                ) : (
                                                    <Visibility style={{ color: "#64748b" }} />
                                                )}
                                            </button>
                                        </div>
                                    )}
                                    {field.name === "password" && passwordValidationError && (
                                        <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "6px", marginBottom: 0 }}>
                                            {passwordValidationError}
                                        </p>
                                    )}
                                    {field.name === "repeatPassword" && passwordMatchValidationError && (
                                        <p style={{ color: "#dc2626", fontSize: "13px", marginTop: "6px", marginBottom: 0 }}>
                                            {passwordMatchValidationError}
                                        </p>
                                    )}
                                </div>
                            );
                        })}

                        {/* Phone field */}
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <label htmlFor="registerUserPhone" style={{ color: "#1e293b" }}>
                                {t('phone')} <span style={{ color: "red" }}> *</span>
                            </label>
                            <PhoneInput
                                country={'us'}
                                countryCodeEditable={false}
                                value={phone}
                                onChange={value => {
                                    setPhone(value);
                                    setPhoneError('');
                                }}
                                inputProps={{
                                    name: 'phone',
                                    required: true,
                                    id: 'registerUserPhone',
                                    style: { flex: 6, borderRadius: "8px", width: '100%', minHeight: '50px', fontSize: '14px', border: "1px solid #e5e7eb" }
                                }}
                                inputStyle={{
                                    width: '100%',
                                    borderRadius: "8px",
                                    minHeight: '50px',
                                    fontSize: '14px',
                                    border: "1px solid #e5e7eb"
                                }}
                                buttonStyle={{
                                    borderRadius: "8px 0 0 8px"
                                }}
                                containerStyle={{
                                    width: '100%'
                                }}
                                specialLabel=""
                                disableDropdown={false}
                                onlyCountries={['us']}
                                onBlur={() => {
                                    if (phone && phone.replace(/\D/g, '').length < 11) setPhoneError(t("toastValidPhone"));
                                }}
                            />
                        </div>

                        {/* SMS opt-in checkbox - aligned with first line of text */}
                        <div className="form-group" style={{ marginBottom: 0 }}>
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                                <input
                                    type="checkbox"
                                    id="smsOptIn"
                                    checked={smsNotificationsOptIn}
                                    onChange={(e) => setSmsNotificationsOptIn(e.target.checked)}
                                    style={{ marginTop: "2px", flexShrink: 0 }}
                                />
                                <div>
                                    <label htmlFor="smsOptIn" style={{ color: "#1e293b", fontSize: "14px", margin: 0, cursor: "pointer", lineHeight: 1.4 }}>
                                        {t('smsOptInCheckboxLabel')}
                                    </label>
                                    <p style={{ color: "#64748b", fontSize: "12px", margin: "6px 0 0 0" }}>
                                        {t('smsOptInHelperText')}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Terms checkbox */}
                        <div className="flex items-center gap-2 mb-4">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id={'termofuse'}
                                checked={formValues.termofuse}
                                onChange={(e) => setFormValues((prev) => ({ ...prev, termofuse: e.target.checked }))}
                            />
                            <p style={{ color: "#1e293b", fontSize: "14px", margin: 0 }}>
                                {t("confimation")}
                                <a href="/politica-de-privacidad" style={{ color: "#4285F4", textDecoration: "underline", margin: "0 4px" }} target="_blank" rel="noopener noreferrer">{t("privacyPolicy")}</a>
                                {t("and")}
                                <a href="/terminos-de-uso" style={{ color: "#4285F4", textDecoration: "underline", margin: "0 4px" }} target="_blank" rel="noopener noreferrer">{t("termOfUse")}</a>.
                            </p>
                        </div>

                        {/* Register Button */}
                        <button
                            className="theme-btn btn-style-one"
                            type="submit"
                            disabled={isLoading || !isRegisterFormValid}
                            style={{
                                backgroundColor: isLoading || !isRegisterFormValid ? "#94a3b8" : "#4285F4",
                                color: "white",
                                width: "100%",
                                padding: "14px 32px",
                                borderRadius: "12px",
                                border: "none",
                                fontSize: "16px",
                                fontWeight: 600,
                                cursor: isLoading || !isRegisterFormValid ? "not-allowed" : "pointer",
                                marginBottom: "8px"
                            }}
                        >
                            {isLoading ? (
                                <div
                                    className="spinner-border"
                                    style={{ height: "15px", width: "15px" }}
                                    role="status"
                                ></div>
                            ) : (
                                t('register')
                            )}
                        </button>

                        {/* Or text */}
                        <div style={{ textAlign: "center", marginBottom: "8px" }}>
                            <span style={{ color: "#64748b", fontSize: "14px" }}>{t("o")}</span>
                        </div>

                        {/* Google Sign Up */}
                        <div style={{ marginBottom: "8px" }}>
                            <GoogleSignUp
                            showSuccessToast={true}
                                type="client"
                                onSuccess={(user) => {
                                    // Check if essential info needs to be completed
                                    const hasPhone = user?.phone && user.phone.trim() !== '';
                                    const isEssentialCompleted = user?.isEssentialCompleted === true;
                                    const needsEssentialInfo = !isEssentialCompleted || !hasPhone;
                                    
                                    if (needsEssentialInfo) {
                                        // Redirect to complete essential info page
                                        navigate("/complete-essential-info", {
                                            state: {
                                                userType: "client"
                                            }
                                        });
                                    } else {
                                        showConfirmationOnce(user?._id || user?.email || "client-google-signup");
                                    }
                                }}
                            />
                        </div>

                        {/* Already have account */}
                        <div style={{ textAlign: "center", marginTop: "8px" }}>
                            <span style={{ fontSize: "14px", color: "#64748b" }}>
                                {t("doYouAlreadyHaveAnAccount")}{" "}
                                <Link
                                    to="/ingresar"
                                    style={{
                                        color: "#4285F4",
                                        textDecoration: "none",
                                        fontWeight: 500,
                                        cursor: "pointer",
                                    }}
                                >
                                    {t("login")}
                                </Link>
                            </span>
                        </div>
                    </div>
                </form>
            </div>
        </div>
        <RegistrationConfirmationModal
            isOpen={showConfirmationModal}
            title={t("signupConfirmation_client_title")}
            intro={t("signupConfirmation_client_intro")}
            buttonLabel={t("close")}
            onPrimaryAction={() => {
                setShowConfirmationModal(false);
                navigate("/");
            }}
        />
        </>
    );
};

export default SignupCustomerPage;
